<template>
  <div>
    <EmulatorSectionHeader title="Styles" />

    <h6 class="header-pretitle mb-3">Mode</h6>
    <div class="btn-group-toggle row gx-2 mb-4">
      <div v-for="mode in themeStyleModes" :key="mode.key" class="col">
        <label
          class="btn w-100 btn-white"
          :class="{ active: mode.key === activeModeKey }"
          @click="setActiveMode(mode.key)"
        >
          {{ mode.title }}
        </label>
      </div>
    </div>

    <div v-for="group in activeModeGroups" :key="group.category">
      <h4 class="mt-4 mb-3">{{ group.category }}</h4>
      <EmulatorCustomizationTextBox
        v-for="style in group.styles"
        :key="style.path"
        :category="`${activeMode.title} - ${style.category}`"
        :label="style.label"
        namespace="theme"
        :path="style.path"
        :dataType="style.dataType"
        :placeholder="placeholderForStyle(style)"
        :validator="validatorForStyle(style)"
      >
        <template v-slot="{ value }">
          <div v-if="style.dataType === 'color'" class="col-auto">
            <ColorSwatch :color="value" />
          </div>
        </template>
      </EmulatorCustomizationTextBox>
    </div>

    <EmulatorCustomizationTextBox
      v-if="customCssEnabled"
      category="Custom Styles"
      label="Custom CSS"
      namespace="theme"
      path="customCSS"
      dataType="string"
      inputType="textarea"
      placeholder="Paste or type your CSS here"
    >
      <template #postInput>
        <Alert type="warning" class="mb-4">
          <p>We cannot guarantee your custom CSS will work as expected.</p>
          <p>
            Custom CSS cannot override certain content, including references to Atomic. Some
            references to Atomic will be automatically applied if you use this feature.
          </p>
          <p class="disclaimer">
            Disclaimer: Our support agreement does not include assistance with modifying or
            debugging your custom CSS.
          </p>
        </Alert>
      </template>
    </EmulatorCustomizationTextBox>
  </div>
</template>

<script>
import Alert from '@/components/MainContent/Alert'
import ColorSwatch from '@/components/MainContent/ColorSwatch'
import EmulatorSectionHeader from '@/components/Modules/Emulator/EmulatorSectionHeader'
import EmulatorCustomizationTextBox from '@/components/Modules/Emulator/CustomizationInputs/EmulatorCustomizationTextBox'
import { mapActions, mapState } from 'vuex'

// Per-mode style fields. Each entry's `subPath` is appended to
// `modes.${activeModeKey}` to produce the final theme path, e.g.
// `modes.light.colors.surface.action.primary`.
const THEME_STYLE_FIELDS = [
  {
    label: 'Color',
    subPath: 'colors.surface.action.primary',
    dataType: 'color',
    category: 'Branding',
  },
  {
    label: 'Background Color',
    subPath: 'components.button.primary.background.default',
    dataType: 'color',
    category: 'Buttons',
  },
  // Disabled until the new theme structure is fully wired up end-to-end.
  // {
  //   label: 'Text Color',
  //   subPath: 'components.button.primary.foreground.default',
  //   dataType: 'color',
  //   category: 'Buttons',
  // },
  {
    label: 'Border Radius',
    subPath: 'components.button.primary.radius',
    dataType: 'string',
    category: 'Buttons',
    placeholder: '4px',
    validator: validatePxLength,
  },
  // Disabled until the new theme structure is fully wired up end-to-end.
  // {
  //   label: 'Border Color',
  //   subPath: 'components.button.primary.border.color',
  //   dataType: 'color',
  //   category: 'Buttons',
  // },
  // {
  //   label: 'Border Width',
  //   subPath: 'components.button.primary.border.width',
  //   dataType: 'string',
  //   category: 'Buttons',
  //   placeholder: '1px',
  //   validator: validatePxLength,
  // },
  // {
  //   label: 'Box Shadow',
  //   subPath: 'components.button.primary.shadow.default',
  //   dataType: 'string',
  //   category: 'Buttons',
  //   placeholder: '0px 1px 2px rgba(0,0,0,1)',
  //   validator: validateBoxShadow,
  // },
  // {
  //   label: 'Color',
  //   subPath: 'components.link.primary.foreground.default',
  //   dataType: 'color',
  //   category: 'Links',
  // },
]

// Shown in any color text input that doesn't yet have a value (or an SDK
// default), so users see the expected format.
const COLOR_PLACEHOLDER = '#000000'

// Validates that a box-shadow value follows the
// `<x>px <y>px <blur>px rgba(R,G,B,A)` shape (numbers can vary; whitespace
// inside the rgba() is tolerated). Returns an error message string when
// invalid, or undefined when valid.
const BOX_SHADOW_PATTERN =
  /^(-?\d+(?:\.\d+)?)px\s+(-?\d+(?:\.\d+)?)px\s+(-?\d+(?:\.\d+)?)px\s+rgba\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*(?:\d+(?:\.\d+)?|\.\d+)\s*\)$/

function validateBoxShadow(value) {
  if (BOX_SHADOW_PATTERN.test(value)) return undefined
  return 'Use the format: 0px 1px 2px rgba(0,0,0,1)'
}

// Validates that a color value is a hex color prefixed with `#`. Accepts the
// CSS-supported lengths: 3, 4, 6, or 8 hex digits (so #fff, #fff8, #ffffff,
// #ffffff80 are all valid). Used as the implicit validator for any field with
// `dataType: 'color'`.
const COLOR_PATTERN = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/

function validateColor(value) {
  if (COLOR_PATTERN.test(value)) return undefined
  return 'Use a hex color starting with # (e.g. #000000)'
}

// Validates that a length value is a non-negative number followed by `px`
// (e.g. `1px`, `0px`, `1.5px`). Used by border radius / border width fields.
const PX_LENGTH_PATTERN = /^\d+(?:\.\d+)?px$/

function validatePxLength(value) {
  if (PX_LENGTH_PATTERN.test(value)) return undefined
  return 'Use a value in px (e.g. 1px)'
}

const THEME_STYLE_MODES = [
  { key: 'light', title: 'Light Mode' },
  { key: 'dark', title: 'Dark Mode' },
]

export default {
  name: 'EmulatorStyles',
  components: {
    Alert,
    ColorSwatch,
    EmulatorSectionHeader,
    EmulatorCustomizationTextBox,
  },
  computed: {
    ...mapState('customer', ['activeCustomer']),
    ...mapState('emulator', ['settings']),
    customCssEnabled() {
      return this.activeCustomer?.features?.customCSS?.enabled
    },
    themeStyleModes() {
      return THEME_STYLE_MODES
    },
    activeModeKey() {
      return this.settings?.theme?.dark ? 'dark' : 'light'
    },
    activeMode() {
      return THEME_STYLE_MODES.find((mode) => mode.key === this.activeModeKey)
    },
    activeModeStyles() {
      return THEME_STYLE_FIELDS.map((field) => ({
        ...field,
        path: `modes.${this.activeModeKey}.${field.subPath}`,
      }))
    },
    activeModeGroups() {
      return this.groupStylesByCategory(this.activeModeStyles)
    },
  },
  methods: {
    ...mapActions('emulator', ['updateDarkMode']),
    setActiveMode(modeKey) {
      const darkMode = modeKey === 'dark'
      if (darkMode === this.settings?.theme?.dark) return
      this.updateDarkMode({ analytics: this.$analytics, darkMode })
    },
    placeholderForStyle(style) {
      if (style.placeholder) return style.placeholder
      if (style.dataType === 'color') return COLOR_PLACEHOLDER
      return undefined
    },
    validatorForStyle(style) {
      if (style.validator) return style.validator
      if (style.dataType === 'color') return validateColor
      return undefined
    },
    groupStylesByCategory(styles) {
      return styles.reduce((groups, style) => {
        let group = groups.find((g) => g.category === style.category)
        if (!group) {
          group = { category: style.category, styles: [] }
          groups.push(group)
        }
        group.styles.push(style)
        return groups
      }, [])
    },
  },
}
</script>

<style scoped lang="scss">
code {
  color: white;
}

a {
  color: white;
  text-decoration: underline;
}

.disclaimer {
  font-size: 12px;
}
</style>
